// src/pages/RegisterTeacher.js

import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import ProgressBar from "react-bootstrap/ProgressBar";
import {
  ShieldCheck,
  User as UserIcon,
  Mail,
  Lock,
  ArrowLeft,
  CheckCircle2,
  CheckCircle,
  XCircle,
} from "lucide-react";

const API_BASE = "https://app.fintogo.ai/api";
//const API_BASE = "http://localhost:4000/api";
const brandBlue = "#1E88E5";
const brandGreen = "#2ED08A";

/* Passwort-Regeln */
function validatePassword(pw) {
  const rules = {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  const passed = Object.values(rules).filter(Boolean).length;
  let strength = "weak";
  if (passed >= 4) strength = "strong";
  else if (passed >= 2) strength = "medium";
  const percent = Math.round((passed / 4) * 100);
  return { rules, passed, strength, percent };
}

function getStrengthProps(strength) {
  if (strength === "strong")
    return { label: "Starkes Passwort", variant: "success", color: "#2ED08A" };
  if (strength === "medium")
    return { label: "Mittlere Stärke", variant: "warning", color: "#f59e0b" };
  return { label: "Schwaches Passwort", variant: "danger", color: "#ef4444" };
}

export default function RegisterTeacher() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activationCode, setActivationCode] = useState("");
  const [codeInfo, setCodeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [pwMismatch, setPwMismatch] = useState(false);

  const pwState = useMemo(() => validatePassword(form.password), [form.password]);
  const strengthProps = getStrengthProps(pwState.strength);

  // Code aus URL übernehmen und validieren
  useEffect(() => {
    const code = searchParams.get("code");
    if (!code) {
      navigate("/register");
      return;
    }

    setActivationCode(code);
    setValidating(true);

    fetch(`${API_BASE}/activation/validate?code=${encodeURIComponent(code)}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data?.ok || data.role !== "teacher") {
          navigate("/register");
          return;
        }
        setCodeInfo(data);
      })
      .catch(() => navigate("/register"))
      .finally(() => setValidating(false));
  }, [searchParams, navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setPwMismatch(false);
    setLoading(true);

    try {
      if (!codeInfo) throw new Error("no_code_info");

      if (form.password !== form.confirm_password) {
        setPwMismatch(true);
        setLoading(false);
        return;
      }

      if (pwState.passed < 4) {
        setError(
          "Bitte erfülle alle Passwortregeln (min. 8 Zeichen, Großbuchstabe, Zahl, Sonderzeichen)."
        );
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_BASE}/auth/register-teacher`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          password: form.password,
          activation_code: activationCode.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "register_failed");
      }

      localStorage.setItem("token", data.token);
      window.location.href = "/TeacherDashboard";
    } catch (err) {
      setError(
        err.message === "email_taken"
          ? "Diese E-Mail-Adresse wird bereits verwendet."
          : "Registrierung fehlgeschlagen. Bitte erneut versuchen."
      );
    }
    setLoading(false);
  };

  if (validating) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{
          minHeight: "100vh",
          background: "linear-gradient(to bottom right, #e0f0ff, white)",
        }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Lädt...</span>
          </div>
          <p className="text-muted">Code wird validiert...</p>
        </div>
      </div>
    );
  }

  if (!codeInfo) {
    return null;
  }

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        background: "linear-gradient(to bottom right, #e0f0ff, white)",
        overflow: "auto",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      }}
    >
      <Container style={{ maxWidth: 900, padding: "2rem 1rem" }}>
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            <Card className="shadow border-0">
              <Card.Body className="p-4 p-md-5">
                {/* Header */}
                <div className="d-flex align-items-center mb-4 gap-3">
                  <div
                    className="rounded-circle d-flex justify-content-center align-items-center"
                    style={{
                      width: 48,
                      height: 48,
                      background: brandBlue,
                    }}
                  >
                    <ShieldCheck className="text-white" size={24} />
                  </div>
                  <div>
                    <h1 className="h4 mb-1">Lehrer-Registrierung</h1>
                    <p className="mb-0 text-muted">
                      Mit Aktivierungscode anmelden und loslernen.
                    </p>
                  </div>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-4">
                    {error}
                  </Alert>
                )}

                {/* Step indicator */}
                <div className="mb-4 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="light" text="dark">
                      Schritt 2 / 2
                    </Badge>
                    <span className="text-muted small">
                      Account-Daten eingeben
                    </span>
                  </div>
                  <div style={{ minWidth: 120 }}>
                    <ProgressBar
                      now={100}
                      variant="success"
                      style={{ height: 6 }}
                    />
                  </div>
                </div>

                <Form onSubmit={handleRegister}>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group controlId="fullName">
                        <Form.Label>Vollständiger Name</Form.Label>
                        <div className="d-flex align-items-center border rounded px-2">
                          <UserIcon className="text-muted me-2" size={18} />
                          <Form.Control
                            type="text"
                            placeholder="Vor- und Nachname"
                            value={form.full_name}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                full_name: e.target.value,
                              }))
                            }
                            className="border-0 shadow-none"
                            required
                          />
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group controlId="email">
                        <Form.Label>E-Mail-Adresse</Form.Label>
                        <div className="d-flex align-items-center border rounded px-2">
                          <Mail className="text-muted me-2" size={18} />
                          <Form.Control
                            type="email"
                            placeholder="name@schule.at"
                            value={form.email}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                email: e.target.value,
                              }))
                            }
                            className="border-0 shadow-none"
                            required
                          />
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="password">
                        <Form.Label>Passwort</Form.Label>
                        <div className="d-flex align-items-center border rounded px-2">
                          <Lock className="text-muted me-2" size={18} />
                          <Form.Control
                            type="password"
                            placeholder="Passwort"
                            value={form.password}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                password: e.target.value,
                              }))
                            }
                            className="border-0 shadow-none"
                            required
                          />
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="confirmPassword">
                        <Form.Label>Passwort bestätigen</Form.Label>
                        <div className="d-flex align-items-center border rounded px-2">
                          <Lock className="text-muted me-2" size={18} />
                          <Form.Control
                            type="password"
                            placeholder="Passwort wiederholen"
                            value={form.confirm_password}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                confirm_password: e.target.value,
                              }))
                            }
                            className="border-0 shadow-none"
                            required
                          />
                        </div>
                        {pwMismatch && (
                          <Form.Text className="text-danger">
                            Die Passwörter stimmen nicht überein.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Passwort-Stärke */}
                  <div className="mt-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small text-muted">
                        Passwort-Stärke: {strengthProps.label}
                      </span>
                    </div>
                    <ProgressBar
                      now={pwState.percent}
                      variant={strengthProps.variant}
                      style={{ height: 6 }}
                    />
                    <div className="d-flex flex-wrap gap-2 mt-2 small">
                      <Badge
                        bg={pwState.rules.length ? "success" : "light"}
                        text={pwState.rules.length ? "light" : "muted"}
                      >
                        {pwState.rules.length ? (
                          <CheckCircle size={14} className="me-1" />
                        ) : (
                          <XCircle size={14} className="me-1" />
                        )}
                        min. 8 Zeichen
                      </Badge>
                      <Badge
                        bg={pwState.rules.upper ? "success" : "light"}
                        text={pwState.rules.upper ? "light" : "muted"}
                      >
                        {pwState.rules.upper ? (
                          <CheckCircle size={14} className="me-1" />
                        ) : (
                          <XCircle size={14} className="me-1" />
                        )}
                        Großbuchstabe
                      </Badge>
                      <Badge
                        bg={pwState.rules.number ? "success" : "light"}
                        text={pwState.rules.number ? "light" : "muted"}
                      >
                        {pwState.rules.number ? (
                          <CheckCircle size={14} className="me-1" />
                        ) : (
                          <XCircle size={14} className="me-1" />
                        )}
                        Zahl
                      </Badge>
                      <Badge
                        bg={pwState.rules.symbol ? "success" : "light"}
                        text={pwState.rules.symbol ? "light" : "muted"}
                      >
                        {pwState.rules.symbol ? (
                          <CheckCircle size={14} className="me-1" />
                        ) : (
                          <XCircle size={14} className="me-1" />
                        )}
                        Sonderzeichen
                      </Badge>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="d-flex justify-content-between mt-4">
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => navigate("/register")}
                    >
                      <ArrowLeft className="me-1" size={16} />
                      Zurück
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        background: brandGreen,
                        borderColor: brandGreen,
                      }}
                    >
                      {loading ? (
                        "Wird gespeichert..."
                      ) : (
                        <>
                          Registrierung abschließen
                          <CheckCircle2 className="ms-2" size={18} />
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
